import {MESSAGE} from "../App/constants.js";
import {ImageMutated} from "../ContentWidgets/ImageMutated.js";
import {JSONGrid} from "../ContentWidgets/JSONGrid.js";
import APIResponseComponent from "../ContentWidgets/APIResponseComponent.js";
import {
    extractHTMLContent,
    fromWebBot,
    generateImageDetectionsId,
    getQueryParamValue,
    isMobile,
    parseJSON
} from "../App/utils.js";
import {JSONComponent} from "../ContentWidgets/JSONComponent.js";
import {RotatingImage} from "../InputTools/ImageToText.js";
import {HTMLToIframe} from "../Item/HTMLToIframe.js";
import React, { Suspense } from "react";
import {CodeViewer} from "../Code/CodeViewer.js";
import axios from "axios";
import * as itemsAPI from "../API/items/itemsAPI";
import {createEmbeddingItem} from "../API/OpenAIAPI";
import * as indexedDB from "../Engines/Dexie";
import * as chatAPI from "../API/chatAPI";
import {kbUserData} from "../utils";
import {executeNodejs} from "../API/CodeExecute";
import ReactPrismjs from "@uiw/react-prismjs";
import {blockAutoscroll, generateMsgId} from "./utils";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {uploadFileAPI} from "../API/KB/Files";
import theme, {colorGreen} from "../App/theme";
import * as Files from "../API/KB/Files";
import {initDB} from "../Engines/Dexie";
import {PictureAsPdf, Download} from "@mui/icons-material";

const LazyBaseDateCalendar = React.lazy(() => import('../Components/BaseDateCalendar'));
const LazyImageEditComponent = React.lazy(() => import('../Image/editor.js'));

const regexImages = /\[?.*\]?\(?(https?:\/\/[^\s]+\.(?:gif|jpe?g|tiff?|png|webp|bmp))\)?/g;

const P = ({ children, ...props }) => <p {...props} style={{ marginTop: '0px', marginBottom: '0px' }}>{children}</p>;

export const contentRender = async ({ contentRenderLoaded, msgIndex, messages, setMessages, iframeRef, KB,
    chatContainerRef, RequestChatAPI, textDetectionsImageFiles, setSystemAlert, sharedChat, setBlockingLoading,
    blockingLoading, sendButtonRef, sendButtonRippleRef, setInputValue, renderSettings, newChatButtonRef}) => {

    let {content, role, msgId} = messages[msgIndex];

    // Strip everything after <!--GEMINI_META (since it's always at the end)
    if (content) {
        const metaTagIndex = content.indexOf('<!--GEMINI_META');
        if (metaTagIndex !== -1) {
            content = content.substring(0, metaTagIndex);
        }
    }

    // if custom render is loaded execute it first
    if (contentRenderLoaded?.onRenderChatMessage) {
        // Create a modified message with stripped content for custom renderer
        const modifiedMessages = [...messages];
        modifiedMessages[msgIndex] = { ...messages[msgIndex], content };

        // #OpenKBS Params onRenderChatMessage
        const res = await contentRenderLoaded.onRenderChatMessage({
            msgIndex, messages: modifiedMessages, setMessages, iframeRef, KB, chatContainerRef, RequestChatAPI, textDetectionsImageFiles,
            setSystemAlert, axios, itemsAPI, createEmbeddingItem, indexedDB, chatAPI, generateMsgId, newChatButtonRef,
            setBlockingLoading, blockingLoading, setInputValue, sendButtonRef, sendButtonRippleRef, kbUserData, executeNodejs,
            initDB, markdownHandler,
            // libs
            ReactPrismjs, CopyToClipboard, APIResponseComponent, uploadFileAPI, blockAutoscroll, theme, CodeViewer,
            Files
        })
        if (res) return res;
        // ... continue to the base render if loaded render does not return response
    }

    let JSONData;
    let JSONDataFromText;
    let JSONDataFromTextPrefix = '';
    const expandJSON = sessionStorage?.getItem?.('generatedAPIKeyData');
    // if (!content) return;
    if (!content) return <br />; // render empty messages

    try {
        JSONData = JSON.parse(content);
    } catch (e) {
        // not JSON
    }
    const isJSONData = typeof JSONData === 'object';
    const elements = [];
    let remainingContent = content;

    try {
        const regex = /(.*?)`?`?`?\s*([\{\[].*?[\}\]])\s*`?`?`?(.*)/s;
        const LinkMarkdownRegex = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g;
        const isNotLinkMarkdown = !remainingContent.match(LinkMarkdownRegex);
        let match;
        let hasMatch = false;
        while (isNotLinkMarkdown && (match = remainingContent.match(regex)) !== null) {
            const prefix = match[1].trim().replace('```json', '');
            const jsonString = match[2];
            remainingContent = match[3].trim();

            JSONDataFromTextPrefix += prefix

            if (prefix) elements.push(<div key={elements.length}>{markdownHandler(prefix)}</div>);

            try {
                JSONDataFromText = JSON.parse(jsonString);
                const JSONCommand = JSONDataFromText;

                if (JSONCommand?.type === MESSAGE.DATE_CALENDAR) {
                    hasMatch = true
                    elements.push(
                        <Suspense fallback={<div></div>} key={elements.length}>
                            <LazyBaseDateCalendar date={JSONCommand.date} />
                        </Suspense>
                    );
                } else if (JSONCommand?.type === MESSAGE.IMAGE_WITH_LINK) {
                    hasMatch = true
                    elements.push(
                        <div>
                            <a href={JSONCommand?.linkUrl} target="_blank" rel="noopener noreferrer" key={elements.length}>
                                <img src={JSONCommand?.imageUrl} width={'50%'} alt={JSONCommand?.altText} />
                            </a>
                        </div>
                    );
                }
                // Add more else if statements as needed
            } catch (e) {
                // Handle error if needed
            }
        }

        if (remainingContent) elements.push(<div key={elements.length}>{remainingContent}</div>);
        if (elements?.length && hasMatch) return <>{elements}</>;
    } catch (e) {
        // Handle error if needed
        console.error(e)
    }


    if (JSONData?.length > 1 && JSONData?.find?.(o => o.type === 'image_url')) {
        return JSONData.map((o,i) => o?.type === 'image_url'
            ? (
                o?.image_url?.url?.toLowerCase?.()?.endsWith?.('.pdf')
                    ? <PictureAsPdf />
                    : <img key={i} src={o?.image_url?.url} width={isMobile ? '100%' : '75%'} />
            )
            : <P key={i}>{o?.text}</P>)
    }

    if (["system", "assistant"].includes(role)) {
        if (isJSONData && JSONData.type === MESSAGE.IMAGE_ROTATION) {
            return <ImageMutated event={JSONData} chatContainerRef={chatContainerRef}/>
        } else if (JSONData?.error) {
            return JSONData?._meta_type
                ? <APIResponseComponent JSONData={JSONData?.data || JSONData}/>
                : JSON.stringify(JSONData?.error)
        } else if (isJSONData && JSONData.type === MESSAGE.JSON_GRID) {
            return <JSONGrid initialData={JSONData.data} translations={JSONData.translations}/>
        } else if (isJSONData && JSONData.type === MESSAGE.SAVED_CHAT_IMAGE) {
            return <img src={JSONData.imageUrl} width={isMobile ? '100%' : '50%'} />
        } else if (isJSONData && JSONData.type === 'CHAT_VIDEO' && JSONData?.data?.videoUrl) {
            return (
                <div>
                    <video controls style={{ width: '100%', maxHeight: '400px' }}>
                        <source src={JSONData.data.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                    <div style={{ marginTop: '8px', textAlign: 'right' }}>
                        <a href={JSONData.data.videoUrl} download>
                            <Download />
                        </a>
                    </div>
                </div>
            )
        } else if (isJSONData && JSONData?._meta_type === MESSAGE.EVENT_FINISHED) {
            // return null on empty EVENT_FINISHED
            if (Object.keys(JSONData).length === 2) return null;
            return  <APIResponseComponent JSONData={JSONData?.data || JSONData}/>;
        } else if (isJSONData && JSONData?._meta_type === MESSAGE.EVENT_STARTED) {
            return (<APIResponseComponent JSONData={{}} loading={true} showAfter={500}/>)
        } else if (typeof JSONData === 'string') {
            return JSONData
        } else if (isJSONData && typeof JSONData.data === 'string') {
            return JSONData.data;
        } else if (JSONDataFromText &&  [MESSAGE.JOB_COMPLETED, MESSAGE.JOB_FAILED].includes(JSONDataFromText?.type))  {
            const res = [<APIResponseComponent entityName='Job' color={colorGreen} open={true} JSONData={JSONDataFromText} />]
            if (JSONDataFromTextPrefix?.trim()?.length) res.unshift(JSONDataFromTextPrefix?.trim())
            return  res
        } else if (isJSONData) {
            return <JSONComponent data={JSONData} collapsed={!expandJSON}/>
        }
    }

    if (role === 'user' && isJSONData && JSONData.type === MESSAGE.TEXT_DETECTIONS) {
        const imageFile = textDetectionsImageFiles?.[generateImageDetectionsId(JSONData.detections)];

        if (JSONData?.view === "JSON") return <JSONComponent data={JSONData} collapsed={!expandJSON} />

        return <RotatingImage
            detections={JSONData?.detections}
            kbId={KB?.kbId}
            imageFile={imageFile}
            // onManualImageRotation={onManualImageRotation}
            leveler={JSONData.isRotated ? null : imageFile?.leveler}
        />
    }

    const maxWidth = !isMobile ? '700px' : undefined

    try {
        const regexMarkdownLinks = /\[(.*?)\]\((http.*?)\)/g;
        const htmlMatch = extractHTMLContent(content)
        if (!getQueryParamValue('disableHTML') && htmlMatch) {
            return <div style={{marginTop: '24px'}}>{HTMLToIframe(htmlMatch, iframeRef)}</div>
        }
        if (content.match(/```/)) {
            let language = null;
            const output = [];

            content.split('\n').forEach(line => {
                if (!language) {
                    language = /```(?<language>\w+)/g.exec(line)?.groups?.language;
                    output.push(language ? {language, code: ''} : line);
                } else if (line.match(/```/)) {
                    language = null;
                } else {
                    output[output.length - 1].code += line + '\n';
                }
            });

            return output.map((o, i) =>
                typeof o === 'string'
                    ? <P key={`chatLine${i}`} style={{fontSize: fromWebBot ? 26 : undefined, maxWidth}}>{o}</P>
                    : <div key={`chatLine${i}`} style={{maxWidth}}>
                        <CodeViewer
                            RequestChatAPI={RequestChatAPI}
                            setInputValue={setInputValue}
                            sendButtonRef={sendButtonRef}
                            msgIndex={msgIndex}
                            messages={messages}
                            KB={KB}
                            limitedWidth={isMobile}
                            execute={!renderSettings?.disableCodeExecuteButton}
                            setSystemAlert={setSystemAlert}
                            className="codeWrapper"
                            language={o.language}
                            source={o.code}
                        />
                    </div>
            );
        } else if (parseJSON(content)?.type === MESSAGE.IMAGE_EDITOR) {
            const withImageEditor = KB?.inputTools?.find( o => parseJSON(o)?.type === 'imageEditor');
            const hasImageEditorInMessages = !!messages?.find(msg => parseJSON(msg.content)?.type === MESSAGE.IMAGE_EDITOR);

            return (withImageEditor || sharedChat && hasImageEditorInMessages)
                ? (
                    <Suspense fallback={<div></div>}>
                        <LazyImageEditComponent
                            RequestChatAPI={RequestChatAPI}
                            msgIndex={msgIndex}
                            messages={messages}
                            setMessages={setMessages}
                            kbId={KB?.kbId}
                            KB={KB}
                        />
                    </Suspense>
                )
                : null
        } else if (content.match(regexImages) && !content?.includes('$imageDelete')) {
            let j = 0;
            const segments = content.split(regexImages);

            return segments.map((segment, i) => segment.match(regexImages)
                ? (<div key={`image_${j++}`} >
                    <img src={segment} alt="" style={{maxWidth: '100%', maxHeight: isMobile ? '200px' : '512px'}} />
                    <br/></div>)
                // : segment.split('\n').map((text, k) => <P key={`${i}-${k}`}>{text}</p>)
                : segment.split('\n').map((text, k) => <P key={`${i}-${k}`}>{markdownHandler(text)}</P>)
            );
        } else if (isJSONData) {
            return <JSONComponent data={JSONData}/>
        }
    } catch (e) {
        console.log('Error processContent', e.toString());
    }

    return content && markdownHandler(content);
};

const renderContent = (content) => {
    return content.map((part, j) => {
        if (part.bold) {
            return <strong key={j}>{part.text}</strong>;
        } else if (part.link) {
            return <a key={j} href={part.href} target="_blank">{part.text}</a>;
        } else {
            return part.text;
        }
    });
};

const markdownHandler = (content) => parseMarkdown(content).map((element, i) => {
    switch (element.type) {
        case 'heading':
            return <h3 key={i}>{renderContent(element.content)}</h3>;
        case 'subheading':
            return <h4 key={i}>{renderContent(element.content)}</h4>;
        case 'listItem':
            return <li key={i}>{renderContent(element.content)}</li>;
        case 'paragraph':
            return <P key={i}>{renderContent(element.content)}</P>;
        default:
            return <P key={i}>{renderContent(element.content)}</P>;
    }
});

const linkRegex = /\[(.*?)\]\((.*?)\)/g;

export const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];

    const processLine = (line, type) => {
        const parts = [];
        let lastIndex = 0;
        let match;

        const boldRegex = /\*\*(.*?)\*\*/g;

        while ((match = boldRegex.exec(line)) !== null) {
            const [fullMatch, boldText] = match;
            const before = line.slice(lastIndex, match.index);
            if (before) parts.push(...processLinks(before));
            parts.push({ text: boldText, bold: true });
            lastIndex = boldRegex.lastIndex;
        }
        const remaining = line.slice(lastIndex);
        if (remaining) parts.push(...processLinks(remaining));
        elements.push({ type, content: parts });
    };

    const processLinks = (text) => {
        const parts = [];
        let remaining = text;
        let match;

        while ((match = linkRegex.exec(remaining)) !== null) {
            const [fullMatch, linkText, href] = match;
            const before = remaining.slice(0, match.index);
            if (before) parts.push({ text: before, bold: false });
            parts.push({ text: linkText, href, link: true });
            remaining = remaining.slice(match.index + fullMatch.length);
        }
        if (remaining) parts.push({ text: remaining, bold: false });

        return parts;
    };

    lines.forEach(line => {
        if (line.startsWith('### ')) {
            processLine(line.replace('### ', ''), 'heading');
        } else if (line.startsWith('#### ')) {
            processLine(line.replace('#### ', ''), 'subheading');
        } else if (line.startsWith('- ')) {
            processLine(line.replace('- ', ''), 'listItem');
        } else {
            processLine(line, 'paragraph');
        }
    });
    return elements;
};